import React, { useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import StorageSummary from './StorageSummary'
import RecentActivity from './RecentActivity'
import FileUpload from './FileUpload'
import FilesTable from './FilesTable'

const API_BASE_URL = 'https://demoserver.botgo.io/chatbotdetector'

const FilesAssetsDashboard: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [textContent, setTextContent] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploadResult, setUploadResult] = useState<{ upload_id: string } | null>(null)
  const [analyzeResult, setAnalyzeResult] = useState<any>(null)

  const handleFileSelect = (files: FileList) => {
    if (files.length > 0) {
      setSelectedFile(files[0])
      // Clear text content when file is selected
      setTextContent('')
      toast.info(`File selected: ${files[0].name}`)
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextContent(e.target.value)
    // Clear selected file when text is entered
    if (e.target.value) {
      setSelectedFile(null)
    }
  }

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('auth-token')
  }

  // Upload file API
  const uploadFile = async (file: File) => {
    const token = getAuthToken()
    const formData = new FormData()
    formData.append('file', file)

    const response = await axios.post(`${API_BASE_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`
      }
    })

    return response.data
  }

  // Analyze URLs API
  const analyzeUrls = async (urls: string[]) => {
    const token = getAuthToken()
    const response = await axios.post(
      `${API_BASE_URL}/analyze`,
      { urls: urls },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    )

    return response.data
  }

  // Analyze with upload_id API (for file upload)
  const analyzeWithUploadId = async (uploadId: string) => {
    const token = getAuthToken()
    const response = await axios.post(
      `${API_BASE_URL}/analyze`,
      { upload_id: uploadId },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    )

    return response.data
  }

  // Parse URLs from text content
  const parseUrls = (text: string): string[] => {
    // Split by newlines, commas, or spaces and filter valid URLs
    const parts = text.split(/[\n,\s]+/).filter(Boolean)

    // Clean and validate URLs
    const urls = parts
      .map((part) => {
        // Remove protocol if present for consistency
        let url = part.trim()
        url = url.replace(/^https?:\/\//, '')
        url = url.replace(/^www\./, '')
        // Remove trailing slashes
        while (url.endsWith('/')) {
          url = url.slice(0, -1)
        }
        return url
      })
      .filter((url) => {
        // Basic URL validation - should contain at least one dot
        return url.includes('.') && url.length > 3
      })

    return urls
  }

  const handleUpdate = async () => {
    if (!selectedFile && !textContent.trim()) {
      toast.warning('Please upload a file or enter URLs to analyze')
      return
    }

    setLoading(true)

    try {
      if (selectedFile) {
        // Handle file upload
        toast.info('Uploading file...')
        const uploadResponse = await uploadFile(selectedFile)

        if (uploadResponse.success && uploadResponse.upload_id) {
          setUploadResult(uploadResponse)
          toast.success(uploadResponse.message || 'File uploaded successfully')
          console.log('Upload result:', uploadResponse)

          // Now analyze with upload_id
          toast.info('Analyzing URLs from file...')
          const analyzeResponse = await analyzeWithUploadId(uploadResponse.upload_id)

          if (analyzeResponse.success) {
            setAnalyzeResult(analyzeResponse)
            toast.success('Analysis completed successfully')
            console.log('Analyze result:', analyzeResponse)
            // Clear selected file after successful analysis
            setSelectedFile(null)
          } else {
            toast.error('Failed to analyze URLs from file')
          }
        } else {
          toast.error('Failed to upload file')
        }
      } else if (textContent.trim()) {
        // Handle URL analysis
        const urls = parseUrls(textContent)

        if (urls.length === 0) {
          toast.warning('No valid URLs found in the text')
          setLoading(false)
          return
        }

        console.log('Analyzing URLs:', urls)
        const result = await analyzeUrls(urls)

        if (result.success) {
          setAnalyzeResult(result)
          toast.success('URLs analyzed successfully')
          console.log('Analyze result:', result)
          // Clear text content after successful analysis
          setTextContent('')
        } else {
          toast.error('Failed to analyze URLs')
        }
      }
    } catch (error: any) {
      console.error('API Error:', error)
      toast.error(error.response?.data?.message || error.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='min-vh-100' style={{ backgroundColor: '#f8f9fa' }}>
      <div className='container-fluid'>
        <div className='row'>
          {/* Main Content */}
          <div className='col-12 col-lg-9 p-4 p-lg-5'>
            {/* Header */}
            <div className='mb-4'>
              {/* Back Link */}

              {/* Title Row */}
              <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3'>
                <h1 className='h3 fw-bold mb-0'>Chatbot Detector</h1>
              </div>
            </div>

            {/* File Upload Section */}
            <div className='mb-4'>
              <FileUpload onFileSelect={handleFileSelect} />
              {selectedFile && (
                <div className='mt-2 d-flex align-items-center gap-2'>
                  <span className='badge bg-success'>Selected:</span>
                  <span className='text-muted small'>{selectedFile.name}</span>
                  <button
                    className='btn btn-sm btn-outline-danger'
                    onClick={() => setSelectedFile(null)}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Or Divider */}
            <div className='d-flex align-items-center mb-4'>
              <div className='flex-grow-1 border-top' style={{ borderColor: '#dee2e6' }}></div>
              <span className='px-3 text-muted fw-medium' style={{ fontSize: '14px' }}>
                or
              </span>
              <div className='flex-grow-1 border-top' style={{ borderColor: '#dee2e6' }}></div>
            </div>

            {/* Textarea Section */}
            <div className='mb-4'>
              <div
                className='border border-2 border-dashed rounded-4 p-4 bg-white'
                style={{ minHeight: '140px' }}
              >
                <textarea
                  value={textContent}
                  onChange={handleTextChange}
                  className='form-control border-0 bg-transparent w-100 h-100'
                  placeholder='Enter URLs to analyze (one per line or comma separated)...&#10;Example: tatamotors.com, sbilife.co.in'
                  style={{
                    minHeight: '100px',
                    resize: 'none',
                    outline: 'none',
                    boxShadow: 'none'
                  }}
                />
              </div>
            </div>

            {/* Update Button */}
            <div className='d-flex align-items-center justify-content-end gap-3 mb-4'>
              <button
                onClick={handleUpdate}
                disabled={loading}
                className='btn text-white d-inline-flex align-items-center gap-2'
                style={{ backgroundColor: '#6366F1' }}
              >
                {loading ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    Processing...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>

            {/* Files Table */}
            <FilesTable searchQuery={searchQuery} analyzeResult={analyzeResult} />
          </div>

          {/* Right Sidebar */}
          <div className='col-12 col-lg-3 bg-white border-start p-4'>
            <StorageSummary analyzeResult={analyzeResult} />
            <RecentActivity />
          </div>
        </div>
      </div>
    </div>
  )
}

export default FilesAssetsDashboard
